/**
 * Attendees-first ordering for person pickers used in a meeting (#15938).
 *
 * The single ranking rule shared by `SearchableSelect` (and so `PersonPicker`)
 * and `TaskAssigneeDropdown`, so the two picker implementations cannot drift:
 *
 * - **Empty query** — only the meeting's attendees, in the order the caller
 *   gave them. Nobody else is listed; the picker shows a "Type to search
 *   everyone" hint instead.
 * - **Non-empty query** — everyone the caller passed (the picker's existing
 *   reach, with its existing visibility rules) that matches, attendees
 *   first. Ties keep their given order on both sides.
 *
 * Pure and dependency-free: it never widens the list it is handed. A private
 * meeting's captain picker passes its permitted candidates, and this only
 * reorders and narrows those.
 */

/** Heading for the attendee group in an attendees-first picker. */
export const ATTENDEE_GROUP_LABEL = 'Attendees'
/** Heading for the non-attendee group, shown only once a query is typed. */
export const NON_ATTENDEE_GROUP_LABEL = 'Everyone else'
/** The hint row an attendees-first picker shows under an empty query. */
export const ATTENDEE_FIRST_HINT = 'Type to search everyone'
/**
 * `data-testid` on an open attendees-first picker's popover. Spelled as a
 * `testId:` field so the golden-path wiring check can find the literal.
 */
export const ATTENDEE_FIRST_PICKER = {
  testId: 'attendee-first-picker',
} as const

export interface AttendeeFirstOptions<T> {
  getId: (item: T) => string
  /** Text matched case-insensitively against the query. */
  getSearchText: (item: T) => string
  /** Person ids of the meeting's attendees. */
  attendeeIds: Iterable<string>
  query: string
}

/** True when `query` has nothing to search on. */
export function isAttendeeFirstQueryEmpty(query: string): boolean {
  return query.trim().length === 0
}

export function rankAttendeesFirst<T>(
  items: readonly T[],
  { getId, getSearchText, attendeeIds, query }: AttendeeFirstOptions<T>,
): T[] {
  const attendees = new Set(attendeeIds)
  const isAttendee = (item: T) => attendees.has(getId(item))
  if (isAttendeeFirstQueryEmpty(query)) return items.filter(isAttendee)

  const q = query.trim().toLowerCase()
  const matched = items.filter(item =>
    getSearchText(item).toLowerCase().includes(q),
  )
  return [
    ...matched.filter(isAttendee),
    ...matched.filter(item => !isAttendee(item)),
  ]
}
